import React, { useState, useEffect, useRef } from 'react';
import { Col, Row, Container, Form, Card } from 'react-bootstrap';
import DashboardChart from '../Components/DashboardChart';
import NavBar from '../Components/NavBar';
import SideNavbar from '../Components/SideNavbar';
import '../index.css';
import CustomLineChart from '../Components/CustomLineChart';
import DiscreteWeekBox from '../Components/DiscreteWeekBox'; // Ensure this matches your file name!
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { analyticsData, getCampaignQuestions } from '../Store/Actions/participant';
import axios from 'axios';
import { FaArrowLeft } from "react-icons/fa";
import Loader from '../Components/Loader';
import { TbReload } from "react-icons/tb";
import GpsGraph from '../Components/GpsGraph';
import { Auth } from 'aws-amplify';
import getApiUrl from '../config';

function ParticipantAnalysis(props) {
    // UI State
    const [alertShow, setAlertShow] = useState([false, ""]);
    const [errorMsg, setErrorMsg] = useState("");
    const [loadingComponents, setLoadingComponents] = useState({
        allQuestions: true,
        numericData: true,
        discreteData: true,
        sensorData: true
    });
    
    // Data State
    const [numericData, setNumericData] = useState({ values: [], labels: [] });
    const [discreteData, setDiscreteData] = useState({ values: [], labels: [] });
    const [sensorData, setSensorData] = useState({ values: [], labels: [] });
    
    // Questions State
    const [numericQuestion, setNumericQuestion] = useState({});
    const [discreteQuestion, setDiscreteQuestion] = useState({});
    const [sensorQuestion, setSensorQuestion] = useState({});
    const [questions, setQuestions] = useState(null);
    const [numericQuestions, setNumericQuestions] = useState([]);
    const [discreteQuestions, setDiscreteQuestions] = useState([]);
    const [sensorQuestions, setSensorQuestions] = useState([]);

    // Other State
    const [dates, setDates] = useState([]);
    const [currentDate, setCurrentDate] = useState("");
    const [flag, setFlag] = useState(true);
    const [avgMetrics, setAvgMetrics] = useState(0);
    const [selectedDay, setSelectedDay] = useState('');
    
    // Insights
    const [numericInsights, setNumericInsights] = useState({ avg: 0, max: 0, trend: "", answered: "" });
    const [discreteInsight, setDiscreteInsight] = useState("");
    const [discreteFrequency, setDiscreteFrequency] = useState([]);
    const [discreteAnswered, setDiscreteAnswered] = useState("");

    const navigate = useNavigate();

    // 🛑 RETRY COUNTER (Prevents Infinite Loops)
    const apiRetries = useRef({
        avg: 0,
        all: 0,
        numeric: 0,
        discrete: 0,
        sensor: 0
    });

    // ✅ AUTH HELPER (Prevents 400/401 Errors)
    const getAuthConfig = async () => {
        try {
            const session = await Auth.currentSession();
            const token = session.getIdToken().getJwtToken();
            return {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': token
                }
            };
        } catch (err) {
            console.error("Auth Error:", err);
            return null;
        }
    };

    // --- DATE LOGIC ---
    const getDateRanges = () => {
        let startDate = new Date(localStorage.getItem("startdate"));
        let endDate = new Date(localStorage.getItem("enddate"));
        const ranges = [];
        let curr = new Date(startDate);
        const now = new Date();
        const stoppingPoint = endDate < now ? endDate : now;

        while (curr.getDay() !== 6) { 
            curr.setDate(curr.getDate() + 1);
        }

        let firstIteration = true;
        while (true) {
            let startOfWeek;
            if (firstIteration) {
                startOfWeek = new Date(startDate);
                firstIteration = false;
            } else {
                startOfWeek = new Date(curr);
                startOfWeek.setDate(startOfWeek.getDate() - 6); 
            }

            const endOfWeek = new Date(curr);
            if (endOfWeek >= stoppingPoint) {
                ranges.push(`${startOfWeek.toLocaleDateString("en-US")} - ${stoppingPoint.toLocaleDateString("en-US")}`);
                break;
            } else {
                ranges.push(`${startOfWeek.toLocaleDateString("en-US")} - ${endOfWeek.toLocaleDateString("en-US")}`);
            }

            curr.setDate(curr.getDate() + 1); 
            if (curr > stoppingPoint) break;
            curr.setDate(curr.getDate() + 6); 
        }
        setDates(ranges);
    };

    useEffect(() => {
        getDateRanges();
    }, []);

    useEffect(() => {
        setCurrentDate(dates.length > 0 ? dates[0] : "");
    }, [dates]);

    // --- INITIALIZATION ---
    useEffect(() => {
        if (currentDate) {
            handleAvgMetrics();
            if (flag) {
                handleAllQuestions();
                setFlag(false);
            } else {
                // Refresh data if date changes
                getNumericData();
                getDiscreteData();
                getSensorData();
            }
        }
    }, [currentDate]);

    // Fetch data when questions change
    useEffect(() => { getNumericData(); }, [numericQuestion]);
    useEffect(() => { getDiscreteData(); }, [discreteQuestion]);
    useEffect(() => { getSensorData(); }, [sensorQuestion]);


    // --- API 1: AVERAGE METRICS ---
    const handleAvgMetrics = async () => {
        const dateRange = currentDate.split(" - ");
        if (dateRange.length < 2) return;

        try {
            const config = await getAuthConfig();
            if (!config) return;

            const requestData = {
                campaign_id: localStorage.getItem("campaignId"),
                participant_id: localStorage.getItem("participantId"),
                start_date: dateRange[0],
                end_date: dateRange[1]
            };

            const response = await axios.post(getApiUrl('newGetParticipantAvgForAnalytics'), requestData, config);

            if (response.data.statusCode && response.data.statusCode !== 200) {
                console.log("Avg Metrics Failed:", response.data);
            } else {
                // Safe Parse: Works if response.data is Object OR String
                const data_res = typeof response.data === 'string' ? JSON.parse(response.data) : response.data;
                
                // Handle Lambda Wrapper if present
                const finalData = data_res.body ? (typeof data_res.body === 'string' ? JSON.parse(data_res.body) : data_res.body) : data_res;

                let avg_comp = 0;
                if (Array.isArray(finalData) && finalData.length > 0) {
                    finalData.forEach((d) => { avg_comp += d.new_compliance });
                    avg_comp = avg_comp / finalData.length;
                }
                setAvgMetrics(avg_comp);
                apiRetries.current.avg = 0; // Success -> Reset Retry
            }
        } catch (err) {
            console.error(err);
            if (apiRetries.current.avg < 2) {
                apiRetries.current.avg += 1;
                setTimeout(() => handleAvgMetrics(), 1000);
            }
        }
    };

    // --- API 2: GET QUESTIONS ---
    const handleAllQuestions = async () => {
        setLoadingComponents(prev => ({ ...prev, allQuestions: true }));
        try {
            const config = await getAuthConfig();
            if (!config) return;

            const requestData = { campaignid: localStorage.getItem("campaignId") };
            const response = await axios.post(getApiUrl('UniqueQuestions'), requestData, config);

            // Safe Parse Logic
            const data = typeof response.data === 'string' ? JSON.parse(response.data) : response.data;
            const finalData = data.body ? (typeof data.body === 'string' ? JSON.parse(data.body) : data.body) : data;

            let discrete = finalData["DiscretePrompt"] || [];
            let numeric = finalData["NumericPrompt"] || [];
            let sensor = finalData["Features"] || [];

            setQuestions({ discrete, numeric, sensor });
            setNumericQuestions(numeric);
            setDiscreteQuestions(discrete);
            setSensorQuestions(sensor);

            // Set Defaults Instantly
            if (numeric.length > 0) setNumericQuestion(numeric[0]);
            if (discrete.length > 0) setDiscreteQuestion(discrete[0]);
            if (sensor.length > 0) setSensorQuestion(sensor[0]);

            setLoadingComponents(prev => ({ ...prev, allQuestions: false }));
            apiRetries.current.all = 0;

        } catch (err) {
            console.error(err);
            if (apiRetries.current.all < 2) {
                apiRetries.current.all += 1;
                setTimeout(() => handleAllQuestions(), 1000);
            } else {
                setLoadingComponents(prev => ({ ...prev, allQuestions: false }));
            }
        }
    };

    // --- API 3: NUMERIC DATA ---
    const getNumericData = async () => {
        const qId = numericQuestion?.questionid || questions?.numeric?.[0]?.questionid;
        if (!qId) return;

        setLoadingComponents(prev => ({ ...prev, numericData: true }));
        let dateRange = currentDate.split(" - ");

        try {
            const config = await getAuthConfig();
            if (!config) return;

            const requestData = {
                campaign_id: localStorage.getItem("campaignId"),
                questionid: qId,
                participant_id: localStorage.getItem("participantId"),
                start_date: dateRange[0],
                end_date: dateRange[1]
            };

            const response = await axios.post(getApiUrl('newNumericDataWithTime'), requestData, config);
            
            // Safe Parse Logic
            const data = typeof response.data === 'string' ? JSON.parse(response.data) : response.data;
            const finalData = data.body ? (typeof data.body === 'string' ? JSON.parse(data.body) : data.body) : data;

            if (!Array.isArray(finalData)) throw new Error("Invalid Data Format");

            let values = [];
            let labels = [];
            finalData.forEach(d => { 
                values.push(d.value); 
                labels.push(d.collectedtimestamp); 
            });
            
            setNumericData({ values, labels });

            // Insights Logic
            if (values.length > 0) {
                const answeredValues = values.filter(val => val !== -1 && val !== "-1").map(Number);
                if (answeredValues.length > 0) {
                    const sum = answeredValues.reduce((a, b) => a + b, 0);
                    const avg = sum / answeredValues.length;
                    const max = Math.max(...answeredValues);
                    
                    let trend = "-";
                    if (answeredValues.length >= 2) {
                        const diff = answeredValues[answeredValues.length - 1] - answeredValues[0];
                        trend = diff > 0 ? "Increasing" : diff < 0 ? "Decreasing" : "Stable";
                    }
                    
                    setNumericInsights({ avg: avg.toFixed(2), max, trend, answered: `${answeredValues.length} / ${values.length}` });
                } else {
                    setNumericInsights({ avg: "-", max: "-", trend: "-", answered: `0 / ${values.length}` });
                }
            } else {
                setNumericInsights({ avg: "-", max: "-", trend: "-", answered: "-" });
            }

            setLoadingComponents(prev => ({ ...prev, numericData: false }));
            apiRetries.current.numeric = 0;

        } catch (err) {
            console.error(err);
            if (apiRetries.current.numeric < 2) {
                apiRetries.current.numeric += 1;
                setTimeout(() => getNumericData(), 1000);
            } else {
                setLoadingComponents(prev => ({ ...prev, numericData: false }));
            }
        }
    };

    // --- API 4: DISCRETE DATA ---
    const getDiscreteData = async () => {
        const qId = discreteQuestion?.questionid || questions?.discrete?.[0]?.questionid;
        if (!qId) return;

        setLoadingComponents(prev => ({ ...prev, discreteData: true }));
        let dateRange = currentDate.split(" - ");

        try {
            const config = await getAuthConfig();
            if (!config) return;

            const requestData = {
                campaign_id: localStorage.getItem("campaignId"),
                questionid: qId,
                participant_id: localStorage.getItem("participantId"),
                start_date: dateRange[0],
                end_date: dateRange[1]
            };

            const response = await axios.post(getApiUrl('newNumericDataWithTime'), requestData, config);

            // Safe Parse Logic
            const data = typeof response.data === 'string' ? JSON.parse(response.data) : response.data;
            const finalData = data.body ? (typeof data.body === 'string' ? JSON.parse(data.body) : data.body) : data;

            if (!Array.isArray(finalData)) throw new Error("Invalid Data Format");

            const discreteResponseData = finalData.map((d) => {
                const value = (d.value !== "-1" && typeof d.value === 'string') ? JSON.parse(d.value) : d.value;
                return { timestamp: d.collectedtimestamp, value: value };
            });

            const answeredCount = discreteResponseData.filter(item => item.value !== "-1").length;
            setDiscreteAnswered(`${answeredCount} / ${discreteResponseData.length}`);
            setDiscreteData({ startOfWeek: dateRange[0], answered: discreteResponseData });

            // Frequency Logic
            try {
                const responseValues = discreteResponseData
                    .filter((item) => item.value !== "-1" && Array.isArray(item.value))
                    .flatMap((item) => item.value);

                const freqMap = {};
                responseValues.forEach(val => { freqMap[val] = (freqMap[val] || 0) + 1; });

                let maxCount = 0;
                let mostFrequent = "N/A";
                Object.entries(freqMap).forEach(([key, count]) => {
                    if (count > maxCount) { maxCount = count; mostFrequent = key; }
                });

                setDiscreteInsight(mostFrequent);
                setDiscreteFrequency(Object.entries(freqMap).map(([label, count]) => ({
                    label, count, total: responseValues.length
                })));

            } catch (e) {
                setDiscreteInsight("N/A");
                setDiscreteFrequency([]);
            }

            setLoadingComponents(prev => ({ ...prev, discreteData: false }));
            apiRetries.current.discrete = 0;

        } catch (err) {
            console.error(err);
            if (apiRetries.current.discrete < 2) {
                apiRetries.current.discrete += 1;
                setTimeout(() => getDiscreteData(), 1000);
            } else {
                setLoadingComponents(prev => ({ ...prev, discreteData: false }));
            }
        }
    };

    // --- API 5: SENSOR DATA ---
    const getSensorData = async () => {
        const qId = sensorQuestion?.questionid || questions?.sensor?.[0]?.questionid;
        if (!qId) return;

        setLoadingComponents(prev => ({ ...prev, sensorData: true }));
        let dateRange = currentDate.split(" - ");

        try {
            const config = await getAuthConfig();
            if (!config) return;

            const requestData = {
                campaign_id: localStorage.getItem("campaignId"),
                questionid: qId,
                participant_id: localStorage.getItem("participantId"),
                start_date: dateRange[0],
                end_date: dateRange[1]
            };

            const response = await axios.post(getApiUrl('newNumericDataWithTime'), requestData, config);

            // Safe Parse Logic
            const data = typeof response.data === 'string' ? JSON.parse(response.data) : response.data;
            const finalData = data.body ? (typeof data.body === 'string' ? JSON.parse(data.body) : data.body) : data;

            if (!Array.isArray(finalData)) throw new Error("Invalid Data Format");

            let values = [];
            let labels = [];

            if (String(qId) === "99") { // GPS Special Case
                let latitudes = [];
                let longitudes = [];
                finalData.forEach(d => {
                    const coords = d.value.split(",");
                    latitudes.push(Number(coords[0]));
                    longitudes.push(Number(coords[1]));
                    labels.push(d.collectedtimestamp);
                });
                values.push(latitudes);
                values.push(longitudes);
            } else {
                finalData.forEach(d => { values.push(d.value); labels.push(d.collectedtimestamp) });
            }

            setSensorData({ values, labels });
            setLoadingComponents(prev => ({ ...prev, sensorData: false }));
            apiRetries.current.sensor = 0;

        } catch (err) {
            console.error(err);
            if (apiRetries.current.sensor < 2) {
                apiRetries.current.sensor += 1;
                setTimeout(() => getSensorData(), 1000);
            } else {
                setLoadingComponents(prev => ({ ...prev, sensorData: false }));
            }
        }
    };

    // --- DOWNLOAD REPORT ---
    const handleDownload = async () => {
        try {
            const config = await getAuthConfig();
            const response = await axios.post(getApiUrl('new_download_report'), {
                dateRange: currentDate,
                participantId: localStorage.getItem("participantId"),
                campaignId: localStorage.getItem("campaignId"),
                managerId: localStorage.getItem("managerId"),
            }, config);

            const body = typeof response.data.body === 'string' ? JSON.parse(response.data.body) : response.data.body;
            
            if (response.status === 200 && body.url) {
                const link = document.createElement('a');
                link.href = body.url;
                link.download = '';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            } else {
                alert(body.error || "File not found");
            }
        } catch (e) {
            console.error(e);
            alert("Error downloading report");
        }
    };

    // --- RENDER HELPERS ---
    const renderScheduleSummary = (schedule) => {
        if (!schedule) return null;
        const dayMap = { '1': 'Sun', '2': 'Mon', '3': 'Tue', '4': 'Wed', '5': 'Thu', '6': 'Fri', '7': 'Sat' };
        const scheduledDays = Object.keys(dayMap).filter(day => Array.isArray(schedule[day]) && schedule[day].length > 0);
        const timesPerWeek = scheduledDays.length;
        const promptsPerDay = scheduledDays.length > 0 ? Math.round(scheduledDays.map(day => schedule[day].length).reduce((a, b) => a + b, 0) / scheduledDays.length) : 0;
        const formattedDays = scheduledDays.map(day => dayMap[day]).join(', ');

        return (
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 16px', fontSize: '14px', fontWeight: '500', color: '#333', backgroundColor: '#f4f7fa', borderRadius: '6px', marginBottom: '10px' }}>
                <div><strong>📅 No.of Times Scheduled:</strong> {timesPerWeek} {formattedDays && `(${formattedDays})`}</div>
                <div><strong>🕒 Prompts per day:</strong> {promptsPerDay}</div>
            </div>
        );
    };

    const getSensorInsights = () => {
        const question = sensorQuestion?.question?.toLowerCase();
        const values = sensorData?.values || [];
        const flatValues = Array.isArray(values[0]) ? values[0] : values;
        const numericValues = flatValues.map(Number).filter(val => !isNaN(val));

        if (!question || numericValues.length === 0) return null;
        const baseStyle = { fontSize: '16px', marginBottom: '10px' };
        
        if (question.includes('battery')) {
            const avg = (numericValues.reduce((a, b) => a + b, 0) / numericValues.length).toFixed(2);
            return <div style={baseStyle}>Avg. Battery %: {avg}%</div>;
        }
        if (question.includes('steps')) {
            const avg = Math.round(numericValues.reduce((a, b) => a + b, 0) / 7);
            return <div style={baseStyle}>Avg. Steps Per Day: {avg}</div>;
        }
        if (question.includes('distance')) {
            const avg = (numericValues.reduce((a, b) => a + b, 0) / 7).toFixed(2);
            return <div style={baseStyle}>Avg. Distance: {avg} m</div>;
        }
        if (question.includes('heart')) {
            const min = Math.round(Math.min(...numericValues));
            const max = Math.round(Math.max(...numericValues));
            const avg = (numericValues.reduce((a, b) => a + b, 0) / numericValues.length).toFixed(2);
            return <div><div style={baseStyle}>Range: {min}-{max} bpm</div><div style={baseStyle}>Avg: {avg} bpm</div></div>;
        }
        return null;
    };

    const filterNumericDataByDay = (values, labels, day) => {
        if (!day) return { values, labels };
        const dayMap = { S: 0, M: 1, T: 2, W: 3, R: 4, F: 5, Sa: 6 };
        const selectedDayNum = dayMap[day];
        const filteredValues = [];
        const filteredLabels = [];
        labels.forEach((label, index) => {
            if (new Date(label).getDay() === selectedDayNum) {
                filteredValues.push(values[index]);
                filteredLabels.push(label);
            }
        });
        return { values: filteredValues, labels: filteredLabels };
    };

    return (
        <div style={{ height: "100%", overflowY: "auto" }} className="bg-image mb-5">
            <NavBar />
            <div className="mb-5" style={{ height: "inherit" }}>
                <Container fluid style={{ height: "100%" }}>
                    <Row style={{ height: "100%" }}>
                        <Col lg={2}>
                            <SideNavbar list={["Participant Information", "Analytics", "Cognitive"]} active={1} links={["/participantDashboard", "/participantAnalysis", "/cognitive"]} />
                        </Col>
                        <Col lg={10} style={{ padding: "20px" }}>
                            <div style={{ display: 'flex', alignItems: 'center', fontSize: '18px', fontWeight: '600', marginBottom: '20px', cursor: 'pointer', color: '#007bff' }} onClick={() => navigate("/campaignDashboard")}>
                                <FaArrowLeft style={{ marginRight: '8px' }} /> Back to Dashboard
                            </div>
                            
                            <Row style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
                                <Col md="auto">
                                    <span style={{ marginRight: "10px" }}>Week Range:</span>
                                    <Form.Select value={currentDate} onChange={(e) => setCurrentDate(e.target.value)} style={{ height: "40px", width: "250px", textAlign: "center" }}>
                                        {dates.map(date => <option key={date} value={date}>{date}</option>)}
                                    </Form.Select>
                                </Col>
                                <Col md="auto" style={{ textAlign: "center" }}>
                                    <h2>{localStorage.getItem("participantId")}</h2>
                                    <div>Participant ID</div>
                                </Col>
                                <Col md="auto" style={{ textAlign: "center" }}>
                                    <h2>{localStorage.getItem("watchID")}</h2>
                                    <div>WatchID</div>
                                </Col>
                                <Col md="auto" style={{ textAlign: "center" }}>
                                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                                        <h2><b>{avgMetrics.toFixed(2)}%</b></h2>
                                        <div>Weekly Avg. Compliance</div>
                                    </div>
                                </Col>
                                <Col md="auto" style={{ display: 'flex', justifyContent: 'center', gap: '12px' }}>
                                    {['S', 'M', 'T', 'W', 'R', 'F', 'Sa'].map((day) => (
                                        <div key={day} style={{ position: 'relative', display: 'inline-block' }}>
                                            <input type="checkbox" checked={selectedDay === day} onChange={(e) => setSelectedDay(e.target.value === selectedDay ? '' : e.target.value)} value={day} id={`day-${day}`} style={{ appearance: 'none', width: '42px', height: '42px', borderRadius: '8px', border: selectedDay === day ? '2px solid #007bff' : '2px solid #ccc', backgroundColor: selectedDay === day ? '#007bff' : '#f9f9f9', cursor: 'pointer' }} />
                                            <label htmlFor={`day-${day}`} style={{ position: 'absolute', top: '0', left: '0', width: '42px', height: '42px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '600', color: selectedDay === day ? '#fff' : '#333', pointerEvents: 'none' }}>{day}</label>
                                        </div>
                                    ))}
                                </Col>
                            </Row>

                            {loadingComponents.allQuestions ? <Loader /> : (
                                <>
                                    <Row className="mt-5">
                                        {/* NUMERIC CARD */}
                                        <Col md={6}>
                                            <Card style={{ backgroundColor: "#fff", padding: "20px", boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)", height: "500px", overflowY: "auto" }}>
                                                <Card.Title style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "8px" }}>
                                                    <span><strong>NUMERIC DATA</strong></span>
                                                    <TbReload onClick={() => getNumericData()} style={{ cursor: "pointer", fontSize: "18px" }} />
                                                </Card.Title>
                                                {numericQuestions.length > 0 ? (
                                                    <>
                                                        <select style={{ borderRadius: "5px", borderColor: "#ccc", width: "100%", padding: "8px", marginBottom: "15px", textAlign: "center", backgroundColor: "#e0e0e0" }} onChange={(e) => setNumericQuestion(JSON.parse(e.target.value))}>
                                                            {numericQuestions.map((ques) => <option key={ques.questionid} value={JSON.stringify(ques)}>{ques.longUIquestion}</option>)}
                                                        </select>
                                                        {renderScheduleSummary(numericQuestion?.schedule)}
                                                        {loadingComponents.numericData ? <Loader /> : (
                                                            <>
                                                                <div style={{ minHeight: "200px" }}>
                                                                    <DashboardChart legends={[numericQuestion?.longUIquestion || ""]} data={[filterNumericDataByDay(numericData.values, numericData.labels, selectedDay).values]} labels={filterNumericDataByDay(numericData.values, numericData.labels, selectedDay).labels} />
                                                                </div>
                                                                <div style={{ borderTop: "1px solid #ccc", marginTop: "15px", paddingTop: "10px", fontSize: "14px", color: "#333" }}>
                                                                    <div><strong>Answered:</strong> {numericInsights.answered}</div>
                                                                    <div><strong>Average:</strong> {numericInsights.avg}</div>
                                                                    <div><strong>Max:</strong> {numericInsights.max}</div>
                                                                    <div><strong>Trend:</strong> {numericInsights.trend}</div>
                                                                </div>
                                                            </>
                                                        )}
                                                    </>
                                                ) : <div>No Numeric Questions</div>}
                                            </Card>
                                        </Col>

                                        {/* DISCRETE CARD */}
                                        <Col md={6}>
                                            <Card style={{ backgroundColor: "#fff", padding: "20px", boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)", height: "500px", overflowY: "auto" }}>
                                                <Card.Title style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "8px" }}>
                                                    <span><strong>DISCRETE DATA</strong></span>
                                                    <TbReload onClick={() => getDiscreteData()} style={{ cursor: "pointer", fontSize: "18px" }} />
                                                </Card.Title>
                                                {discreteQuestions.length > 0 ? (
                                                    <>
                                                        <select style={{ borderRadius: "5px", borderColor: "#ccc", width: "100%", padding: "8px", marginBottom: "15px", textAlign: "center", backgroundColor: "#e0e0e0" }} onChange={(e) => setDiscreteQuestion(JSON.parse(e.target.value))}>
                                                            {discreteQuestions.map((ques) => <option key={ques.questionid} value={JSON.stringify(ques)}>{ques.longUIquestion}</option>)}
                                                        </select>
                                                        {renderScheduleSummary(discreteQuestion?.schedule)}
                                                        {loadingComponents.discreteData ? <Loader /> : (
                                                            <>
                                                                <DiscreteWeekBox question={discreteQuestion} data={discreteData} selectedDay={selectedDay} />
                                                                <div style={{ borderTop: "1px solid #ccc", marginTop: "15px", paddingTop: "10px", fontSize: "14px", color: "#333" }}>
                                                                    <div><strong>Most Frequent:</strong> {discreteInsight}</div>
                                                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                                                        {discreteFrequency.map((item) => <div key={item.label} style={{ marginLeft: '5px' }}>{item.label}: {((item.count / item.total) * 100).toFixed(1)}%</div>)}
                                                                    </div>
                                                                    <div><strong>Answered:</strong> {discreteAnswered}</div>
                                                                </div>
                                                            </>
                                                        )}
                                                    </>
                                                ) : <div>No Discrete Questions</div>}
                                            </Card>
                                        </Col>
                                    </Row>

                                    {/* SENSOR DATA */}
                                    <Row className="mt-5">
                                        <Col md={12}>
                                            <Card className="mt-5" style={{ width: "100%", background: "rgba(255,255,255,0.8)", padding: "5px" }}>
                                                <Card.Title style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "8px" }}>
                                                    <span><strong>SENSOR DATA</strong></span>
                                                    <TbReload onClick={() => getSensorData()} style={{ cursor: "pointer", fontSize: "18px" }} />
                                                </Card.Title>
                                            </Card>
                                            <Card className="mt-3" style={{ width: "100%", height: "450px", background: "rgba(255,255,255,0.8)", padding: "5px", overflow: "hidden", display: "flex", flexDirection: "column" }}>
                                                <select style={{ border: "none", textAlign: "center", fontSize: "22px" }} onChange={(e) => setSensorQuestion(JSON.parse(e.target.value))}>
                                                    {sensorQuestions.map((question) => <option key={question.questionid} value={JSON.stringify(question)}>{question.question}</option>)}
                                                </select>
                                                {loadingComponents.sensorData ? <Loader /> : (
                                                    <div style={{ display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden' }}>
                                                        <div style={{ flex: 3, height: '100%', maxHeight: '100%', overflow: 'hidden', paddingRight: '10px' }}>
                                                            {String(sensorQuestion.questionid) === "99" ? <GpsGraph legends={[sensorQuestion.question]} data={sensorData.values} labels={sensorData.labels} /> : <CustomLineChart legends={[sensorQuestion.question]} data={[sensorData.values]} labels={sensorData.labels} />}
                                                        </div>
                                                        <div style={{ flex: 1, paddingLeft: '20px', borderLeft: '1px solid #ccc', display: 'flex', flexDirection: 'column', justifyContent: 'center', fontSize: '14px', color: '#333' }}>
                                                            {getSensorInsights()}
                                                        </div>
                                                    </div>
                                                )}
                                            </Card>
                                        </Col>
                                    </Row>
                                </>
                            )}
                        </Col>
                    </Row>
                </Container>
            </div>
        </div>
    );
}

const mapStateToProps = (state) => {
    return {
        user: state.user,
        campaign: state.campaign,
        participant: state.participant,
        manager: state.manager,
        state: state.state
    }
}

const mapDispatchToProps = (dispatch) => {
    return {
        getParticipantAnalysis: (managerId, campaignId, participantId) => dispatch(analyticsData(managerId, campaignId, participantId)),
        getCampaignQuestions: (campaignId) => dispatch(getCampaignQuestions(campaignId))
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(ParticipantAnalysis);